import { useState, type SyntheticEvent } from "react";
import { ApiError, type Backend, type ServerSettings, type TaskApi } from "../api/taskApi";

export const DEFAULT_SERVER_URL = "http://127.0.0.1:8484";

interface LoginScreenProps {
  backend: Backend;
  saved: ServerSettings | undefined;
  notice?: string;
  onSignedIn(api: TaskApi): void;
}

export function describeSignInFailure(failure: unknown, backendKind: Backend["kind"]): string {
  if (!(failure instanceof ApiError)) return "Could not sign in.";
  switch (failure.code) {
    case "unauthorized":
      return "The username or password was not accepted.";
    case "unreachable":
      return backendKind === "web"
        ? "Could not reach the server. Check the address, and that the server was started with --allow-origin for this page's origin."
        : "Could not reach the server. Check the address and your connection.";
    case "unavailable":
      return "The server is busy or unavailable. Try again shortly.";
    case "other":
      return failure.message;
  }
}

export function LoginScreen({ backend, saved, notice, onSignedIn }: LoginScreenProps) {
  const [serverUrl, setServerUrl] = useState(saved?.serverUrl ?? DEFAULT_SERVER_URL);
  const [username, setUsername] = useState(saved?.username ?? "");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setBusy(true);
    setFailure(undefined);
    try {
      onSignedIn(await backend.signIn({ serverUrl: serverUrl.trim(), username: username.trim(), password }));
    } catch (error) {
      setFailure(describeSignInFailure(error, backend.kind));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login">
      <form className="login-card" onSubmit={(event) => void submit(event)}>
        <h1>Sign in to td</h1>
        <p>Connect to your td server. Your tasks stay available offline once synced.</p>
        {notice !== undefined && <p>{notice}</p>}
        {failure !== undefined && (
          <p className="login-error" role="alert">
            {failure}
          </p>
        )}
        <div className="field">
          <label htmlFor="server-url">Server address</label>
          <input
            id="server-url"
            className="text-input"
            type="url"
            required
            autoComplete="url"
            value={serverUrl}
            onChange={(event) => setServerUrl(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="username">Username</label>
          <input
            id="username"
            className="text-input"
            required
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            className="text-input"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <button type="submit" className="button primary" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </main>
  );
}
